import { useRef, useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDismissable } from '../hooks/useDismissable';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../types';

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDismissable(containerRef, () => setOpen(false), open);

  const current = (
    SUPPORTED_LANGUAGES as readonly string[]
  ).includes(i18n.language)
    ? (i18n.language as SupportedLanguage)
    : 'en';

  const choose = (lng: SupportedLanguage) => {
    void i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
        className="flex h-9 items-center gap-2 rounded-full border border-line bg-surface/80 px-3 text-sm text-ink transition hover:bg-surface"
      >
        <Globe size={16} aria-hidden />
        <span className="font-medium uppercase tracking-wider">{current}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('language.label')}
          className="absolute right-0 top-11 z-popover min-w-[180px] overflow-hidden rounded-md border border-line bg-surface shadow-xl"
        >
          {SUPPORTED_LANGUAGES.map((lng) => (
            <li key={lng}>
              <button
                type="button"
                role="option"
                aria-selected={current === lng}
                onClick={() => choose(lng)}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition hover:bg-bg ${
                  current === lng ? 'text-ink' : 'text-muted'
                }`}
              >
                <span>{LANGUAGE_LABELS[lng]}</span>
                {current === lng && <Check size={14} aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
